import React, { createContext, useContext, useState, useEffect } from 'react';

const ComparisonContext = createContext();

export const ComparisonProvider = ({ children }) => {
  const [comparisonItems, setComparisonItems] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Load current user and their comparison items
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    if (currentUser?.id) {
      setCurrentUserId(currentUser.id);
      const userComparison = localStorage.getItem(`jrweb_user_${currentUser.id}_comparison`);
      if (userComparison) {
        try {
          setComparisonItems(JSON.parse(userComparison));
        } catch (error) {
          console.error('Error loading comparison items:', error);
          setComparisonItems([]);
        }
      }
    } else {
      // For non-logged-in users, use general comparison
      const generalComparison = localStorage.getItem('jrweb_comparison');
      if (generalComparison) {
        try {
          setComparisonItems(JSON.parse(generalComparison));
        } catch (error) {
          console.error('Error loading comparison items:', error);
          setComparisonItems([]);
        }
      }
    }
  }, []);

  // Save comparison to localStorage whenever it changes
  useEffect(() => {
    if (currentUserId) {
      localStorage.setItem(`jrweb_user_${currentUserId}_comparison`, JSON.stringify(comparisonItems));
    } else {
      localStorage.setItem('jrweb_comparison', JSON.stringify(comparisonItems));
    }
  }, [comparisonItems, currentUserId]);

  // Update user ID when user logs in/out
  useEffect(() => {
    const handleStorageChange = () => {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      if (currentUser?.id !== currentUserId) {
        if (currentUser?.id) {
          setCurrentUserId(currentUser.id);
          const userComparison = localStorage.getItem(`jrweb_user_${currentUser.id}_comparison`);
          if (userComparison) {
            try {
              setComparisonItems(JSON.parse(userComparison));
            } catch (error) {
              setComparisonItems([]);
            }
          } else {
            setComparisonItems([]);
          }
        } else {
          setCurrentUserId(null);
          setComparisonItems([]);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentUserId]);

  const addToComparison = (product) => {
    setComparisonItems(prev => {
      // Check if product already exists
      if (prev.find(item => item.id === product.id)) {
        return prev;
      }
      
      // Limit to 4 items for comparison
      if (prev.length >= 4) {
        return [...prev.slice(1), product];
      }
      
      return [...prev, product];
    });
  };

  const removeFromComparison = (productId) => {
    setComparisonItems(prev => prev.filter(item => item.id !== productId));
  };

  const clearComparison = () => {
    setComparisonItems([]);
    if (currentUserId) {
      localStorage.removeItem(`jrweb_user_${currentUserId}_comparison`);
    } else {
      localStorage.removeItem('jrweb_comparison');
    }
  };

  const isInComparison = (productId) => {
    return comparisonItems.some(item => item.id === productId);
  };

  const value = {
    comparisonItems,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isInComparison,
    comparisonCount: comparisonItems.length
  };

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
};

export default ComparisonContext;
